import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowRight, Folder, FolderOpen, ChevronRight, HardDrive, Loader2, Users } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { googleDriveApi } from '../../services/google-drive-api';

interface DriveFolder {
  id: string;
  name: string;
}

type DriveLocation = 'my-drive' | 'shared-drives';

const GoogleDriveSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Connection status
  const [status, setStatus] = useState<'loading' | 'success' | 'folder-picker' | 'syncing' | 'done' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [dataSourceId, setDataSourceId] = useState<string | null>(null);

  // Drive location toggle
  const [driveLocation, setDriveLocation] = useState<DriveLocation>('my-drive');
  const [sharedDrives, setSharedDrives] = useState<DriveFolder[]>([]);
  const [selectedSharedDrive, setSelectedSharedDrive] = useState<DriveFolder | null>(null);
  const [loadingSharedDrives, setLoadingSharedDrives] = useState(false);

  // Folder picker
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DriveFolder | null>(null);

  // Sync
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const success = searchParams.get('success');
    const dsId = searchParams.get('dataSourceId');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'Failed to connect to Google Drive');
    } else if (success === 'true' && dsId) {
      setDataSourceId(dsId);
      setStatus('success');
      setMessage('Successfully connected to Google Drive');
      setTimeout(() => {
        setStatus('folder-picker');
        loadFolders(dsId);
        loadSharedDrives(dsId);
      }, 1500);
    } else {
      setStatus('success');
      setMessage('Google Drive connection established');
    }
  }, [searchParams]);

  const loadSharedDrives = async (dsId: string) => {
    setLoadingSharedDrives(true);
    try {
      const response = await googleDriveApi.listSharedDrives(dsId);
      setSharedDrives(response.drives);
    } catch (error) {
      console.error('Failed to load shared drives:', error);
      setSharedDrives([]);
    } finally {
      setLoadingSharedDrives(false);
    }
  };

  const loadFolders = async (dsId: string, parentId?: string, driveId?: string) => {
    setLoadingFolders(true);
    try {
      const response = await googleDriveApi.listFolders(dsId, parentId, driveId);
      setFolders(response.folders);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleSwitchToMyDrive = () => {
    if (!dataSourceId) return;
    setDriveLocation('my-drive');
    setSelectedSharedDrive(null);
    setFolderPath([]);
    setSelectedFolder(null);
    loadFolders(dataSourceId);
  };

  const handleSwitchToSharedDrives = () => {
    setDriveLocation('shared-drives');
    setSelectedSharedDrive(null);
    setFolderPath([]);
    setSelectedFolder(null);
    setFolders([]);
  };

  const handleSelectSharedDrive = (drive: DriveFolder) => {
    if (!dataSourceId) return;
    setSelectedSharedDrive(drive);
    setFolderPath([]);
    setSelectedFolder(null);
    loadFolders(dataSourceId, undefined, drive.id);
  };

  const handleFolderNavigate = (folder: DriveFolder) => {
    if (!dataSourceId) return;
    setFolderPath([...folderPath, folder]);
    setSelectedFolder(null);
    const driveId = selectedSharedDrive?.id;
    loadFolders(dataSourceId, folder.id, driveId);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (!dataSourceId) return;
    const driveId = selectedSharedDrive?.id;

    if (index === -1) {
      // Root of current drive
      setFolderPath([]);
      setSelectedFolder(null);
      if (driveLocation === 'shared-drives' && selectedSharedDrive) {
        loadFolders(dataSourceId, undefined, driveId);
      } else {
        loadFolders(dataSourceId);
      }
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setSelectedFolder(null);
      loadFolders(dataSourceId, newPath[newPath.length - 1].id, driveId);
    }
  };

  const triggerSync = async (folderId: string, folderName: string) => {
    if (!dataSourceId) return;
    try {
      await googleDriveApi.selectFolder(dataSourceId, folderId, folderName);
      setStatus('syncing');
      setSyncMessage(`Syncing files from "${folderName}"...`);

      const result = await googleDriveApi.syncNow(dataSourceId);
      setSyncMessage(result.message);
      setStatus('done');
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncMessage('Sync encountered an error. You can retry from the integrations page.');
      setStatus('done');
    }
  };

  const handleSelectFolder = () => {
    if (!selectedFolder) return;
    triggerSync(selectedFolder.id, selectedFolder.name);
  };

  const handleSelectCurrentFolder = () => {
    if (folderPath.length > 0) {
      const current = folderPath[folderPath.length - 1];
      triggerSync(current.id, current.name);
    } else if (driveLocation === 'shared-drives' && selectedSharedDrive) {
      // Sync the root of the shared drive
      triggerSync(selectedSharedDrive.id, selectedSharedDrive.name);
    }
  };

  const rootLabel = driveLocation === 'shared-drives' && selectedSharedDrive
    ? selectedSharedDrive.name
    : 'My Drive';

  const canSyncCurrentFolder = folderPath.length > 0 || (driveLocation === 'shared-drives' && selectedSharedDrive);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8">
        <div className="text-center">
          {/* Loading */}
          {status === 'loading' && (
            <>
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Processing Connection...
              </h2>
              <p className="text-muted-foreground">
                Please wait while we complete the setup
              </p>
            </>
          )}

          {/* Success (brief) */}
          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 bg-success/15 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Connection Successful!
              </h2>
              <p className="text-muted-foreground mb-4">
                {message}
              </p>
              {dataSourceId ? (
                <p className="text-sm text-muted-foreground">
                  Loading folder picker...
                </p>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => navigate('/sources/integrations')}
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  className="w-full mt-4"
                >
                  Back to Integrations
                </Button>
              )}
            </>
          )}

          {/* Folder Picker */}
          {status === 'folder-picker' && (
            <div className="text-left">
              <h2 className="text-xl font-bold text-foreground mb-1 text-center">
                Select a Folder to Sync
              </h2>
              <p className="text-sm text-muted-foreground mb-4 text-center">
                Choose which Google Drive folder to sync documents from
              </p>

              {/* Drive location toggle */}
              <div className="flex rounded-lg border mb-4 overflow-hidden">
                <button
                  onClick={handleSwitchToMyDrive}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                    driveLocation === 'my-drive'
                      ? 'bg-info/15 text-info'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <HardDrive className="w-4 h-4" />
                  My Drive
                </button>
                <button
                  onClick={handleSwitchToSharedDrives}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border-l transition-colors ${
                    driveLocation === 'shared-drives'
                      ? 'bg-info/15 text-info'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Shared Drives
                  {sharedDrives.length > 0 && (
                    <span className="text-xs bg-muted rounded-full px-1.5">
                      {sharedDrives.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Shared drives list (when in shared drives mode and no drive selected yet) */}
              {driveLocation === 'shared-drives' && !selectedSharedDrive && (
                <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
                  {loadingSharedDrives ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="md" />
                    </div>
                  ) : sharedDrives.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      No shared drives found
                    </div>
                  ) : (
                    sharedDrives.map((drive) => (
                      <div
                        key={drive.id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => handleSelectSharedDrive(drive)}
                      >
                        <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm text-foreground truncate">
                          {drive.name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Folder browser (My Drive or inside a shared drive) */}
              {(driveLocation === 'my-drive' || selectedSharedDrive) && (
                <>
                  {/* Breadcrumb */}
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3 flex-wrap">
                    {driveLocation === 'shared-drives' && (
                      <>
                        <button
                          onClick={handleSwitchToSharedDrives}
                          className="hover:text-info font-medium"
                        >
                          Shared Drives
                        </button>
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                      </>
                    )}
                    <button
                      onClick={() => handleBreadcrumbClick(-1)}
                      className="hover:text-info font-medium"
                    >
                      {rootLabel}
                    </button>
                    {folderPath.map((f, i) => (
                      <React.Fragment key={f.id}>
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                        <button
                          onClick={() => handleBreadcrumbClick(i)}
                          className="hover:text-info font-medium truncate max-w-[120px]"
                        >
                          {f.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Folder list */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
                    {loadingFolders ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="md" />
                      </div>
                    ) : folders.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        No subfolders found
                      </div>
                    ) : (
                      folders.map((folder) => (
                        <div
                          key={folder.id}
                          className={`flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-b-0 ${
                            selectedFolder?.id === folder.id ? 'bg-info/15' : ''
                          }`}
                          onClick={() => setSelectedFolder(folder)}
                          onDoubleClick={() => handleFolderNavigate(folder)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {selectedFolder?.id === folder.id ? (
                              <FolderOpen className="w-4 h-4 text-info flex-shrink-0" />
                            ) : (
                              <Folder className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            )}
                            <span className="text-sm text-foreground truncate">
                              {folder.name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderNavigate(folder);
                            }}
                            className="text-muted-foreground hover:text-foreground flex-shrink-0"
                            title="Open folder"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mb-4">
                    Click to select a folder. Double-click or use the arrow to navigate into it.
                  </p>
                </>
              )}

              {/* Actions */}
              <div className="space-y-2">
                {selectedFolder && (
                  <Button
                    variant="primary"
                    onClick={handleSelectFolder}
                    className="w-full"
                  >
                    Sync "{selectedFolder.name}"
                  </Button>
                )}
                {!selectedFolder && canSyncCurrentFolder && (
                  <Button
                    variant="primary"
                    onClick={handleSelectCurrentFolder}
                    className="w-full"
                  >
                    Sync current folder: "{folderPath.length > 0 ? folderPath[folderPath.length - 1].name : selectedSharedDrive?.name}"
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => navigate('/sources/integrations')}
                  className="w-full"
                >
                  Skip - Choose folder later
                </Button>
              </div>
            </div>
          )}

          {/* Syncing */}
          {status === 'syncing' && (
            <>
              <Loader2 className="w-12 h-12 text-info animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Syncing Documents...
              </h2>
              <p className="text-muted-foreground">
                {syncMessage}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This may take a few minutes depending on the number of files
              </p>
            </>
          )}

          {/* Done */}
          {status === 'done' && (
            <>
              <div className="mx-auto w-16 h-16 bg-success/15 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Setup Complete
              </h2>
              <p className="text-muted-foreground mb-6">
                {syncMessage}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/sources/integrations')}
                rightIcon={<ArrowRight className="w-4 h-4" />}
                className="w-full"
              >
                Back to Integrations
              </Button>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-destructive/15 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Connection Failed
              </h2>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/sources/integrations')}
                className="w-full"
              >
                Try Again
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GoogleDriveSuccessPage;
